import {
  Box,
  Button,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlinePets } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import ClinicPickerDialog from "../../../../components/dialogs/ClinicPickerDialog";
import { ComplexIconButtonAlert } from "../../../../components/display/SimpleIconAlert";
import TitleWithIcon from "../../../../components/display/TitleWithIcon";
import CustomDialog from "../../../../components/wrappers/CustomDrawer";
import { ThemeContext } from "../../../../contexts/ThemeContext";
import ToggleDrawer from "../../../../contexts/ToggleDrawer";
import { ADD_PET } from "../../../../enums/ApiPaths";
import { PETS } from "../../../../enums/QueryKeys";
import useUser from "../../../../hooks/useUser";
import { isArrayEmpty, useIsDesktop } from "../../../../utils/Functions";
import AddPetForm from "../../../pets/form/AddPetForm";
import useGetHomePageUserPets from "../../hooks/useGetHomePageUserPets";
import PetListCard from "./PetListCard";
import NoClinicWrapper from "../../../../components/wrappers/NoClinicWrapper";

const CONSTANT_PET_LIMIT = 4;

export default function UserHomePagePets() {
  const { t } = useTranslation();
  const { mode } = useContext(ThemeContext);
  const light = String(mode) === "light";
  const { profile, currentClinic } = useUser();
  const navigate = useNavigate();
  const mutationKey = PETS;

  const { data, isLoading, error } = useGetHomePageUserPets(profile.id);
  const petRenderLimit = CONSTANT_PET_LIMIT;

  const pets = data?.pets ?? [];
  const totalPetsLength = data?.total ?? 0;

  const isDesktop = useIsDesktop();

  const gridItemProps = useMemo(() => {
    if (isDesktop) {
      return { xs: 12, sm: 6, md: 12 };
    } else {
      const countToRender = Math.min(pets.length, petRenderLimit);

      if (isLoading || countToRender === 0) {
        return { xs: 12, sm: 6, md: 4 };
      }
      switch (countToRender) {
        case 1:
          return { xs: 12 };
        case 2:
          return { xs: 12, sm: 6 };
        case 3:
          return { xs: 12, sm: 4 };
        default:
          return { xs: 12, sm: 3, md: 3 };
      }
    }
  }, [isDesktop, isLoading, petRenderLimit, pets.length]);

  function goToMyPets() {
    navigate(ADD_PET);
  }

  return (
    <Paper
      sx={{
        p: 2,
        height: "100%",
        minHeight: "100px",
        maxHeight: { sm: "600px", md: "1200px" },
        bgcolor: light ? "white" : "",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box>
        <Stack mb={2} justifyContent={"space-between"} direction="row">
          <TitleWithIcon title={"Pets"}>
            <MdOutlinePets />
          </TitleWithIcon>
          {!currentClinic.id ? null : (
            <Box mt={{ sm: 1, md: 0 }}>
              <ToggleDrawer>
                <CustomDialog
                  color="info"
                  buttonSize={"medium"}
                  buttonVariant="contained"
                  buttonMessage={t("NewPet")}
                  testId="homepage.pets.add-btn"
                >
                  <AddPetForm mutationKey={mutationKey} />
                </CustomDialog>
              </ToggleDrawer>
            </Box>
          )}
        </Stack>
      </Box>
      <Divider />
      <Box sx={{ flexGrow: 1, overflowY: "visible" }}>
        {/*Loading */}
        {isLoading && (
          <Grid container spacing={2} sx={{ mt: 2, height: "95%" }}>
            <Grid item xs={12} sm={12} md={12} mt="auto" mb="auto">
              <LinearProgress />
            </Grid>
          </Grid>
        )}

        {/* Error Alert */}
        {!isLoading && error && (
          <Grid container spacing={2} sx={{ mt: 2, height: "95%" }}>
            <Grid item xs={12} sm={12} md={12} mt="auto" mb="auto">
              <ComplexIconButtonAlert
                message="ErrorFetchingPets"
                severity="error"
                icon={<MdOutlinePets fontSize="inherit" />}
              />
            </Grid>
          </Grid>
        )}
        {/* No pets alert */}
        {!isLoading && !error && isArrayEmpty(pets) && (
          <Grid container spacing={2} sx={{ mt: 2, height: "95%" }}>
            <Grid item xs={12} sm={12} md={12} mt="auto" mb="auto">
              <ComplexIconButtonAlert
                message={
                  !currentClinic.id
                    ? "ChooseAClinicBeforeUsingThisFeature"
                    : "YouHaveNoPetsSaved"
                }
                severity="info"
                icon={<MdOutlinePets fontSize="inherit" />}
              >
                <NoClinicWrapper>
                  <ToggleDrawer>
                    <CustomDialog
                      color="info"
                      buttonSize={"small"}
                      buttonVariant="contained"
                      buttonMessage={t("NewPet")}
                      testId="homepage.pets.empty-state-add-btn"
                    >
                      <AddPetForm mutationKey={mutationKey} />
                    </CustomDialog>
                  </ToggleDrawer>
                </NoClinicWrapper>
              </ComplexIconButtonAlert>
            </Grid>
          </Grid>
        )}
        {!isLoading && !error && !isArrayEmpty(pets) && (
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {(pets || []).slice(0, petRenderLimit).map((pet) => (
              <Grid item key={pet.id} minWidth={"160px"} {...gridItemProps}>
                <PetListCard pet={pet} testId={`homepage.pets.card.${pet.id}`}/>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {!isLoading && !error && Number(totalPetsLength) > petRenderLimit && (
        <Grid container>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="center" sx={{ my: 0.5 }}>
              <Typography variant="body2" sx={{ pt: 0.6 }}>
                {Math.min(petRenderLimit, totalPetsLength)} / {totalPetsLength}
              </Typography>
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  goToMyPets();
                }}
              >
                {t("ViewAll")}
              </Button>
            </Box>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
}
